import { HeartPulse, Moon, CalendarDays, Smartphone, Scissors, Droplets, Timer, Repeat, Clock, Footprints, Ruler, Grip, Activity, Salad, BedDouble, Sunset, Coffee, Eye, BellOff, BookOpen, CalendarCheck, Smile, GlassWater, Sun } from "lucide-react";
import { full, smart, decimal } from "@/lib/format";

const YEAR_DAYS = 365.25;
const LIFE_EXPECTANCY = 80;

const ICONS = { HeartPulse, Moon, CalendarDays, Smartphone, Scissors, Droplets };

export const CALCULATORS = [
  {
    slug: "latidos",
    title: "Latidos del corazón",
    short: "Latidos",
    subtitle: "¿Cuántas veces ha latido tu corazón desde que naciste?",
    description:
      "Calcula el número total de contracciones cardíacas de tu vida según tu edad y tu pulso en reposo.",
    unit: "latidos",
    accent: "#D9381E",
    icon: "HeartPulse",
    bentoSpan: "md:col-span-12 lg:col-span-8",
    funFact:
      "Un corazón promedio bombea unos 7.200 litros de sangre al día: una piscina pequeña cada mes.",
    inputs: [
      { key: "age", label: "Tu edad", type: "slider", min: 1, max: 99, step: 1, defaultValue: 28, unit: "años" },
      { key: "hr", label: "Pulso en reposo", type: "slider", min: 40, max: 110, step: 1, defaultValue: 70, unit: "ppm" },
    ],
    compute(v) {
      const beatsPerDay = 1440 * v.hr;
      const daysLived = v.age * YEAR_DAYS;
      const soFar = daysLived * beatsPerDay;
      const toEighty = Math.max(0, (LIFE_EXPECTANCY - v.age)) * YEAR_DAYS * beatsPerDay;
      const bloodL = soFar * 0.07;
      return {
        big: { value: soFar, label: "latidos desde que naciste", format: full },
        rows: [
          { label: "Latidos al día", value: beatsPerDay, format: full },
          { label: "Latidos hasta los 80", value: toEighty, format: smart },
          { label: "Sangre bombeada hasta hoy", value: bloodL, suffix: " L", format: smart },
        ],
        equivalences: [
          { icon: Droplets, title: `${decimal(bloodL / 30000, 1)} cisternas`, text: "de 30.000 L: la sangre que ha bombeado tu corazón cabría en estas cubas giratorias." },
          { icon: Timer, title: `${full(beatsPerDay)} al día`, text: "tu corazón no descansa ni un segundo: es el músculo más constante de tu cuerpo." },
        ],
        chart: {
          title: "Latidos acumulados por década vivida",
          data: [0, 1, 2, 3, 4, 5, 6, 7].slice(0, Math.ceil(v.age / 10)).map((d) => ({
            name: `${d * 10}–${d * 10 + 9}`,
            value: Math.round(Math.min(10, v.age - d * 10) * YEAR_DAYS * beatsPerDay),
          })),
        },
        method: {
          formula: "latidos = días vividos × 1.440 minutos/día × pulso en reposo",
          sources: "La media adulta en reposo se sitúa entre 60 y 100 ppm; cada sístole mueve ~70 ml de sangre.",
          factors: ["Deporte y medicación bajan el pulso en reposo", "El pulso varía con la edad y el estrés", "Es una estimación de divulgación, no un diagnóstico"],
        },
      };
    },
    defaults() {
      return { age: 28, hr: 70 };
    },
    improveTitle: "Un corazón que late más despacio, tarda más",
    improve: [
      { icon: Activity, title: "Cardio 150 min/semana", text: "Caminar rápido, nadar o ir en bici baja tu pulso en reposo 5-15 ppm: son años de latidos ahorrados." },
      { icon: Salad, title: "Menos sal, más potasio", text: "Bajar la sal y sumar plátano, espinacas o alubias mantiene la tensión y protege el músculo cardíaco." },
      { icon: BedDouble, title: "Dormir 7-8 horas", text: "Una mala noche dispara tu pulso del día siguiente. Dormir bien es el mantenimiento más barato del corazón." },
    ],
  },
  {
    slug: "sueno",
    title: "Años soñando",
    short: "Sueño",
    subtitle: "El tiempo que llevas viviendo en el mundo de los sueños",
    description:
      "Averigua cuántos años enteros has dormido y cuántas historias has soñado sin enterarte.",
    unit: "años durmiendo",
    accent: "#2B4C3F",
    icon: "Moon",
    bentoSpan: "md:col-span-12 lg:col-span-4",
    funFact:
      "A los 75 años habrás pasado unos 25 años durmiendo y unos 6 soñando intensamente.",
    inputs: [
      { key: "age", label: "Tu edad", type: "slider", min: 1, max: 99, step: 1, defaultValue: 28, unit: "años" },
      { key: "hours", label: "Horas de sueño por noche", type: "slider", min: 3, max: 12, step: 0.5, defaultValue: 7.5, unit: "h" },
    ],
    compute(v) {
      const yearsSlept = (v.age * v.hours) / 24;
      const nights = v.age * YEAR_DAYS;
      const dreams = nights * 4;
      const remYears = yearsSlept * 0.25;
      const toEighty = Math.max(0, (LIFE_EXPECTANCY - v.age) * v.hours) / 24;
      return {
        big: { value: yearsSlept, label: "años de tu vida dormido", format: (n) => decimal(n, 1) },
        rows: [
          { label: "Noches dormidas", value: nights, format: smart },
          { label: "Sueños vividos (≈4/noche)", value: dreams, format: smart },
          { label: "Años en fase REM", value: remYears, suffix: " años", format: (n) => decimal(n, 1) },
          { label: "Te esperan hasta los 80", value: toEighty, suffix: " años más", format: (n) => decimal(n, 1) },
        ],
        equivalences: [
          { icon: Moon, title: `${decimal(remYears, 1)} años soñando`, text: "la fase REM concentra los sueños vívidos: un cuarto de todo lo que dormiste." },
          { icon: Repeat, title: `${smart(dreams)} sueños`, text: "si cada sueño durara una película, necesitarías 9 vidas para verlas todas." },
        ],
        chart: {
          title: "Tu noche típica repartida",
          data: [
            { name: "Ligero", value: +(v.hours * 0.5).toFixed(2) },
            { name: "Profundo", value: +(v.hours * 0.25).toFixed(2) },
            { name: "REM", value: +(v.hours * 0.25).toFixed(2) },
          ],
        },
        method: {
          formula: "años dormidos = edad × horas de sueño ÷ 24",
          sources: "El adulto medio duerme entre 7 y 8 horas; la fase REM ocupa cerca del 25% del sueño y solemos soñar 4-6 veces por noche.",
          factors: ["Turnos de trabajo y siestas alteran el cómputo", "La proporción de REM baja con la edad", "No recuerdas la mayoría de tus sueños"],
        },
      };
    },
    defaults() {
      return { age: 28, hours: 7.5 };
    },
    improveTitle: "Dormir mejor, no solo dormir más",
    improve: [
      { icon: Sunset, title: "Luz por la mañana", text: "Diez minutos de luz natural al levantarte anclan tu reloj interno: por la noche el sueño llega solo." },
      { icon: Coffee, title: "Cafeína antes de las 14 h", text: "La cafeína tarda hasta 8 horas en irse del cuerpo: el café de las 17 h sigue ahí a medianoche." },
      { icon: Moon, title: "Habitación fría y oscura", text: "18 °C, oscuridad total y sin pantallas: la combinación que acorta el tiempo que tardas en dormirte." },
    ],
  },
  {
    slug: "domingos",
    title: "Días y domingos",
    short: "Domingos",
    subtitle: "Los domingos que ya viviste y los que te quedan",
    description:
      "Una mirada estoica a la finitud del tiempo: ponle número a tus domingos.",
    unit: "domingos restantes",
    accent: "#C47B2B",
    icon: "CalendarDays",
    bentoSpan: "md:col-span-6 lg:col-span-5",
    funFact:
      "Si vives hasta los 80, tendrás 4.160 domingos en total. Cada uno cuenta.",
    inputs: [
      { key: "birth", label: "Fecha de nacimiento", type: "date", defaultValue: null },
      { key: "life", label: "Esperanza de vida", type: "slider", min: 60, max: 100, step: 1, defaultValue: 83, unit: "años" },
    ],
    compute(v) {
      const birth = new Date(v.birth + "T00:00:00");
      const now = new Date();
      const daysLived = Math.floor((now - birth) / 86400000);
      const sundaysLived = Math.floor(daysLived / 7);
      const totalDays = v.life * YEAR_DAYS;
      const sundaysLeft = Math.max(0, Math.floor((totalDays - daysLived) / 7));
      return {
        big: { value: sundaysLeft, label: "domingos por disfrutar", format: full },
        rows: [
          { label: "Días vividos", value: daysLived, format: full },
          { label: "Horas vividas", value: daysLived * 24, format: smart },
          { label: "Domingos vividos", value: sundaysLived, format: full },
          { label: "Domingos de una vida entera", value: Math.floor(totalDays / 7), format: full },
        ],
        equivalences: [
          { icon: Clock, title: `${full(daysLived * 24)} horas`, text: "es todo el tiempo que llevas aquí. La pregunta no es cuánto, sino qué." },
          { icon: CalendarDays, title: `${full(sundaysLeft)} mañanas`, text: "de café lento, paseos y planes que aún puedes elegir cómo gastar." },
        ],
        chart: {
          title: "Tus domingos: vividos frente a restantes",
          data: [
            { name: "Vividos", value: sundaysLived },
            { name: "Restantes", value: sundaysLeft },
          ],
        },
        method: {
          formula: "domingos ≈ días ÷ 7 · restantes = (esperanza de vida × 365,25 − días vividos) ÷ 7",
          sources: "Usamos la media redondeada de la esperanza de vida en España, hoy superior a 80 años.",
          factors: ["El cálculo cuenta los domingos reales solo de forma aproximada (1 cada 7 días)", "Tu esperanza real depende de salud y genética", "No es una predicción: es un recordatorio"],
        },
      };
    },
    defaults() {
      const d = new Date();
      d.setFullYear(d.getFullYear() - 28);
      return { birth: d.toISOString().slice(0, 10), life: 83 };
    },
    improveTitle: "Domingos que recuperan, no que apagan",
    improve: [
      { icon: CalendarCheck, title: "Diseña el domingo el jueves", text: "Un domingo sin plan se lo come el móvil. Con apuntar tres ideas el jueves basta para salvarlo." },
      { icon: Footprints, title: "Naturaleza obligatoria", text: "Dos horas semanales al aire libre reducen el estrés medido: es receta médica en algunos países." },
      { icon: Smile, title: "Un plan con gente", text: "El vínculo social es el mejor predictor de una vejez sana. Invita a alguien a caminar contigo." },
    ],
  },
  {
    slug: "pantallas",
    title: "Tiempo en pantallas",
    short: "Pantallas",
    subtitle: "Años de tu vida mirando píxeles a un palmo de la cara",
    description:
      "Cuantifica los años que dedicarás a pantallas si mantienes tus hábitos actuales.",
    unit: "años en pantallas",
    accent: "#3B5284",
    icon: "Smartphone",
    bentoSpan: "md:col-span-12 lg:col-span-7",
    funFact:
      "Con 6 horas diarias de móvil, una persona pasará cerca de 15 años de su vida adulta mirando una pantalla.",
    inputs: [
      { key: "age", label: "Tu edad", type: "slider", min: 1, max: 99, step: 1, defaultValue: 28, unit: "años" },
      { key: "hours", label: "Horas al día frente a pantallas", type: "slider", min: 0.5, max: 14, step: 0.5, defaultValue: 6.5, unit: "h" },
    ],
    compute(v) {
      const yearsScreen = (v.age * v.hours) / 24;
      const days = v.age * YEAR_DAYS;
      const unlocks = days * 96;
      const scrollKm = (days * 90) / 1000;
      const toEighty = Math.max(0, (LIFE_EXPECTANCY - v.age) * v.hours) / 24;
      return {
        big: { value: yearsScreen, label: "años de tu vida en pantallas", format: (n) => decimal(n, 1) },
        rows: [
          { label: "Días completos de pantalla", value: (days * v.hours) / 24, format: smart },
          { label: "Veces que desbloqueaste el móvil", value: unlocks, format: smart },
          { label: "Kilómetros de scroll con el pulgar", value: scrollKm, suffix: " km", format: (n) => decimal(n, 0) },
          { label: "Te esperan hasta los 80", value: toEighty, suffix: " años más", format: (n) => decimal(n, 1) },
        ],
        equivalences: [
          { icon: Footprints, title: `${decimal(scrollKm, 0)} km de scroll`, text: "podrías ir andando de Madrid a Barcelona… varias veces, con el dedo." },
          { icon: Grip, title: `${smart(unlocks)} desbloqueos`, text: "96 al día de media: cada uno una puerta a otra media hora perdida." },
        ],
        chart: {
          title: "Años de pantalla acumulados por edad",
          data: [10, 20, 30, 40, 50, 60, 70].map((a) => ({
            name: `${a}`,
            value: +(((a * v.hours) / 24) || 0).toFixed(1),
          })),
        },
        method: {
          formula: "años en pantalla = edad × horas diarias ÷ 24",
          sources: "La media europea ronda las 5-7 h diarias de uso total de dispositivos y unos 96 desbloqueos móviles al día.",
          factors: ["El trabajo de oficina dispara las horas acumuladas", "La distancia de scroll varía con la aplicación", "Estimación divulgativa: mide hábitos, no adicciones"],
        },
      };
    },
    defaults() {
      return { age: 28, hours: 6.5 };
    },
    improveTitle: "Que la pantalla te devuelva tiempo",
    improve: [
      { icon: Eye, title: "Regla 20-20-20", text: "Cada 20 minutos, mira algo a 6 metros durante 20 segundos. Ojos y cuello lo agradecen el mismo día." },
      { icon: BellOff, title: "Notificaciones al mínimo", text: "Cada aviso cuesta unos 20 minutos de vuelta al foco. Desactiva todas las que no hayas elegido tú." },
      { icon: BookOpen, title: "Estante junto al sofá", text: "Sustituir el scroll nocturno por papel devuelve horas de sueño sin necesidad de fuerza de voluntad." },
    ],
  },
  {
    slug: "cabello-unas",
    title: "Cabello y uñas",
    short: "Cabello",
    subtitle: "Metros de pelo y uñas que ha fabricado tu cuerpo",
    description:
      "El sorprendente rendimiento continuo de tu queratina: la longitud total producida en tu vida.",
    unit: "km de pelo producidos",
    accent: "#8C3B68",
    icon: "Scissors",
    bentoSpan: "md:col-span-12 lg:col-span-5",
    funFact:
      "El cabello crece unos 0,35 mm al día: más de 12 cm al año por cada uno de tus 100.000 folículos.",
    inputs: [
      { key: "age", label: "Tu edad", type: "slider", min: 1, max: 99, step: 1, defaultValue: 28, unit: "años" },
      { key: "speed", label: "Velocidad de tu pelo", type: "slider", min: 0.8, max: 1.8, step: 0.05, defaultValue: 1.25, unit: "cm/mes" },
    ],
    compute(v) {
      const perStrandCmYear = v.speed * 12;
      const follicles = 100000;
      const kmTotal = (follicles * v.age * perStrandCmYear) / 100000;
      const mPerStrand = (v.age * perStrandCmYear) / 100;
      const nailTrims = v.age * 12 * 2 * 10;
      const nailCm = v.age * 3.6 * 10;
      return {
        big: { value: kmTotal, label: "km de cabello producidos en total", format: (n) => decimal(n, 0) },
        rows: [
          { label: "Metros por cada pelo (si nunca lo cortaras)", value: mPerStrand, suffix: " m", format: (n) => decimal(n, 1) },
          { label: "Folículos trabajando a la vez", value: follicles, format: full },
          { label: "Cortes de uñas en tu vida", value: nailTrims, format: smart },
          { label: "Centímetros de uñas crecidos", value: nailCm, suffix: " cm", format: smart },
        ],
        equivalences: [
          { icon: Ruler, title: `${decimal(kmTotal, 0)} km de queratina`, text: "entre todos tus folículos: más distancia que la que separa París de Ámsterdam." },
          { icon: Scissors, title: `${smart(nailTrims)} uñas cortadas`, text: "dos veces al mes, diez dedos, toda la vida: una tarea constante e invisible." },
        ],
        chart: {
          title: "Kilómetros de cabello acumulados con la edad",
          data: [10, 20, 30, 40, 50, 60, 70].map((a) => ({
            name: `${a}`,
            value: +(((follicles * a * perStrandCmYear) / 100000) || 0).toFixed(0),
          })),
        },
        method: {
          formula: "km totales = 100.000 folículos × edad × (cm/mes × 12) ÷ 100.000",
          sources: "El cabello crece de media 1,25 cm al mes y las uñas de las manos unos 3,6 cm al año.",
          factors: ["Genética, hormonas y estación del año alteran la velocidad", "El pelo se cae y se reemplaza: aquí lo contamos como producción", "Cada folículo trabaja de forma independiente"],
        },
      };
    },
    defaults() {
      return { age: 28, speed: 1.25 };
    },
    improveTitle: "Queratina de primera categoría",
    improve: [
      { icon: Salad, title: "Proteína y hierro de verdad", text: "El pelo es proteína sólida: huevos, legumbre y hierro se notan en el folículo unos meses después." },
      { icon: Sun, title: "Sol con cabeza", text: "El UV degrada la fibra capilar y envejece el cuero cabelludo. Gorra en verano, siempre." },
      { icon: Activity, title: "Tabaco fuera", text: "Fumar reduce el flujo sanguíneo del folículo y acelera el envejecimiento visible de pelo y uñas." },
    ],
  },
  {
    slug: "agua",
    title: "Agua bebida",
    short: "Agua",
    subtitle: "Los litros que han pasado por ti desde que naciste",
    description:
      "Tu cuerpo es una desembocadura constante: calcula los litros de agua que has bebido en toda tu vida.",
    unit: "litros bebidos",
    accent: "#1D7A8C",
    icon: "Droplets",
    bentoSpan: "md:col-span-12 lg:col-span-7",
    funFact:
      "Beber un litro al día durante 80 años suma unos 29.000 litros: una cisterna entera pasando por ti.",
    inputs: [
      { key: "age", label: "Tu edad", type: "slider", min: 1, max: 99, step: 1, defaultValue: 28, unit: "años" },
      { key: "glasses", label: "Vasos de agua al día (250 ml)", type: "slider", min: 0.5, max: 10, step: 0.5, defaultValue: 3, unit: "vasos" },
    ],
    compute(v) {
      const litersPerDay = v.glasses * 0.25;
      const days = v.age * YEAR_DAYS;
      const soFar = days * litersPerDay;
      const perYear = 365.25 * litersPerDay;
      const toEighty = Math.max(0, LIFE_EXPECTANCY - v.age) * YEAR_DAYS * litersPerDay;
      return {
        big: { value: soFar, label: "litros de agua bebidos hasta hoy", format: full },
        rows: [
          { label: "Litros al año", value: perYear, format: full },
          { label: "Vasos en tu vida", value: days * v.glasses, format: smart },
          { label: "Te espera hasta los 80", value: toEighty, suffix: " L más", format: smart },
          { label: "Botellas de litro y medio", value: soFar / 1.5, format: smart },
        ],
        equivalences: [
          { icon: Droplets, title: `${smart(soFar / 150)} bañeras`, text: "de 150 litros: con todo el agua que has bebido podrías llenarlas una a una." },
          { icon: GlassWater, title: `${full(litersPerDay)} L al día`, text: "tu media diaria: el riñón la ajusta al milímetro para que nunca falte ni sobre." },
        ],
        chart: {
          title: "Litros de agua acumulados por edad",
          data: [10, 20, 30, 40, 50, 60, 70].map((a) => ({
            name: `${a}`,
            value: Math.round(a * 365.25 * litersPerDay),
          })),
        },
        method: {
          formula: "litros = días vividos × vasos al día × 0,25 L",
          sources: "La referencia habitual son 2-3 litros de bebida al día, de los que el agua debería ser la mayor parte; usamos vasos de 250 ml.",
          factors: ["Clima, deporte y constitución cambian tus necesidades", "Sopa, fruta y café también cuentan como hidratación", "Estimación divulgativa, no una recomendación médica"],
        },
      };
    },
    defaults() {
      return { age: 28, glasses: 3 };
    },
    improveTitle: "Hidratarte sin pensarlo",
    improve: [
      { icon: GlassWater, title: "Un vaso al despertar", text: "Repones la pérdida de la noche y arrancas el día hidratado antes del primer café." },
      { icon: Coffee, title: "Agua antes que café", text: "Convierte cada café o té en una pareja de vasos de agua: la costumbre se pega sola." },
      { icon: Salad, title: "Fruta que es agua", text: "Sandía, pepino, naranja o calabacín suman hidratación y potasio en el mismo bocado." },
    ],
  },
];

export const bySlug = (slug) => CALCULATORS.find((c) => c.slug === slug);
export { ICONS };
